<template>
  <OnboardingLayout
    title="Payment Summary"
    description="Join the thousands of Arnergy users all over Africa who are enjoying uninterrupted power supply with our solar solution."
    @onButtonClick="submitForm"
    imageDescription="System ownership is transferred upon meeting all lease obligations"
    :imageUrl="require('@/assets/img/female-wave.gif')"
    :canContinue="!showCustomLoading && canContinue"
    :loading="showLoading"
  >
    <div class="summary-container">
      <div class="summary">
        <div v-if="showLoading">loading, please wait...</div>
        <el-form
          ref="form"
          :model="formData"
          :rules="{
            numInverters: [
              {
                required: true,
                message: 'Please enter the number of inverters',
                trigger: 'blur'
              }
            ],
            downPaymentPercentage: [
              {
                required: true,
                message: 'Please select the down payment percentage',
                trigger: 'change'
              },
              { validator: validateDownPayment, trigger: 'change' }
            ],
            installationAddress: [
              {
                required: true,
                message: 'Please enter the installation address',
                trigger: 'blur'
              }
            ],
            customerRegion: [
              {
                required: true,
                message:
                  'Please select the closest installation address region',
                trigger: 'change'
              }
            ],
            buildingType: [
              {
                required: true,
                message: 'Please select the building type',
                trigger: 'change'
              }
            ],
            numMonths: [
              {
                required: true,
                message: 'Please enter the number of months',
                trigger: 'blur'
              },
              {
                type: 'number',
                min: 6,
                max: 36,
                message: 'Number of months must be between 6 and 36',
                trigger: 'blur'
              }
            ]
          }"
        >
          <div class="grid">
            <el-form-item label="Number of Inverters" prop="numInverters">
              <el-input
                v-model="formData.numInverters"
                @change="checkCanContinue"
                type="number"
              ></el-input>
            </el-form-item>
            <el-form-item label="Region" prop="customerRegion">
              <el-select
                v-model="formData.customerRegion"
                @change="handleChangeCustomerRegion"
                placeholder="Select"
              >
                <el-option
                  v-for="location in locations"
                  :key="location.key"
                  :label="location.value"
                  :value="location.key"
                ></el-option>
              </el-select>
            </el-form-item>
          </div>
          <el-form-item label="Installation Address" prop="installationAddress">
            <el-input
              v-model="formData.installationAddress"
              @change="checkCanContinue"
            ></el-input>
          </el-form-item>
          <el-form-item
            label="Down Payment Percentage"
            prop="downPaymentPercentage"
            label-position="top"
          >
            <el-slider
              v-model="formData.downPaymentPercentage"
              :min="0"
              @change="handleChangeDownPaymentPercentage"
              :max="downPaymentMax"
              :step="10"
            ></el-slider>
          </el-form-item>
          <div class="grid">
            <el-form-item label="Building Type" prop="buildingType">
              <el-select
                v-model="formData.buildingType"
                @change="handleChangeCustomerRegion"
                placeholder="Select"
              >
                <el-option
                  label="Storage Only"
                  value="storage_only"
                ></el-option>
                <el-option label="Bungalow" value="bungalow"></el-option>
                <el-option label="1 Storey" value="1_storey"></el-option>
                <el-option label="2 Storey" value="2_storey"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="Number of Months" prop="numMonths">
              <el-input-number
                v-model="formData.numMonths"
                :min="6"
                @change="handleChangeNoOfMonth"
                :max="36"
                controls-position="right"
              ></el-input-number>
            </el-form-item>
          </div>
        </el-form>

        <div class="cost-loading" v-if="showCustomLoading">
          Calculating cost, please wait... <a-spin />
        </div>

        <div class="summary-table">
          <div>
            <p>Down Payment Amount</p>
            <p>
              NGN
              {{ Intl.NumberFormat("en-US").format(downPaymentAmount) }}
            </p>
          </div>
          <div>
            <p>Installation Cost</p>
            <p>
              NGN
              {{ Intl.NumberFormat("en-US").format(installationCost) }}
            </p>
          </div>
          <!-- <div>
            <p>Caution Fee</p>
            <p>
              NGN
              {{
                Intl.NumberFormat("en-US").format(
                  3 * +activeProduct.monthlyAmountLO * +formData.numInverters
                )
              }}
            </p>
          </div> -->
          <!-- <div>
            <p>First Month Payment</p>
            <p>
              NGN
              {{
                Intl.NumberFormat("en-US").format(
                  3 * monthlyPayment
                )
              }}
            </p>
          </div> -->
          <div>
            <p>Logistics Cost</p>
            <p>
              NGN
              {{ Intl.NumberFormat("en-US").format(logisticsCost) }}
            </p>
          </div>
          <div>
            <p>Total Upfront Amount</p>
            <p>
              NGN
              {{ Intl.NumberFormat("en-US").format(upfrontPayment) }}
            </p>
          </div>
          <div>
            <p>Starting Monthly Payment</p>
            <p>
              NGN
              {{ Intl.NumberFormat("en-US").format(monthlyPayment) }}
            </p>
          </div>
          <!-- <div class="bold-text">
                <p>Starting Monthly Payments</p>
                <p>
                  NGN {{ Intl.NumberFormat("en-US").format(monthlyPaymentValue) }}
                </p>
              </div> -->
        </div>
      </div>
    </div>
  </OnboardingLayout>
</template>

<script>
import { mapGetters } from "vuex";
import OnboardingLayout from "@/components/layouts/onboarding-layout.vue";
import { Form, FormItem, Slider } from "element-ui";
import locationsData from "@/data/location.json";
import CalculatorService from "@/services/calculator.js";
import OnboardingService from "@/services/onboarding.js";
import { Spin } from "ant-design-vue";

const default_down_payment_percent = 20;
const default_num_months = 6;

export default {
  components: {
    OnboardingLayout,
    "el-form": Form,
    "el-form-item": FormItem,
    "el-slider": Slider,
    "a-spin": Spin
  },
  computed: {
    ...mapGetters(["getUserSession"])
  },
  created() {
    this.locations = locationsData;
  },
  watch: {
    // logisticsCost(newValue, oldValue) {
    //   return this.calculateLogisticsCost();
    // },

    // 'formData.downPaymentPercentage': function(newValue, oldValue) {
    //   this.calculateTotalUpfront()
    // },
    // "formData.installationAddress": function(newValue, oldValue) {
    //   this.calculateTotalUpfront();
    // },
    quantity(newValues, oldValues) {
      if (!newValues) return;
      // this.activeProduct = {
      //   firstPaymentAmt: 3 * +this.loAmount * newValues,
      //   cautionFee: +this.loAmount * newValues,
      //   monthlyPaymentValue: +this.loAmount * newValues
      // };
    }
  },
  data() {
    return {
      formData: {
        numInverters: 1,
        installationAddress: "",
        customerRegion: "",
        downPaymentPercentage: default_down_payment_percent, //important
        buildingType: "",
        numMonths: default_num_months
      },
      nextPageName: "BVNCheck",
      activeProduct: {},
      logisticsCost: 0,
      showLoading: false,
      showCustomLoading: false,
      canContinue: false,
      downPaymentMax: 100,
      downPaymentAmount: 0,
      installationCost: 0,
      monthlyPayment: 0,
      upfrontPayment: 0
    };
  },
  methods: {
    async checkCanContinue() {
      try {
        if (
          this.showCustomLoading ||
          !this.formData.installationAddress ||
          !this.formData.customerRegion ||
          !this.formData.buildingType
        ) {
          this.canContinue = false;
          return;
        }
        this.showCustomLoading = true;
        const res = await Promise.all([
          this.calculateLogisticsCost(),
          this.calculateDownPaymentAmount(),
          this.calculateTotalUpfront(),
          this.calculateInstallationCost(),
          this.calculateMonthly()
        ]);

        // this.showCustomLoading = false;
        if (res) {
          this.canContinue = true;
        }
      } catch (error) {
        this.canContinue = false;
      } finally {
        this.showCustomLoading = false;
      }
    },
    async handleChangeNoOfMonth() {
      this.checkCanContinue();
    },
    async handleChangeCustomerRegion() {
      this.checkCanContinue();
    },
    async handleChangeDownPaymentPercentage() {
      this.checkCanContinue();
    },
    async calculateLogisticsCost() {
      if (!this.formData.customerRegion || !this.formData.buildingType) {
        this.logisticsCost = 0;
      }
      try {
        const res = await CalculatorService.logisticsCost({
          arnergyProductId: this.activeProduct.id,
          customerRegion: this.formData.customerRegion,
          buildingType: this.formData.buildingType,
          numInverters: this.formData.numInverters
        });
        this.logisticsCost = res.data;
        // await this.calculateTotalUpfront();
      } catch (error) {
        this.logisticsCost = 0;
      }
    },
    async calculateTotalUpfront() {
      if (
        !this.formData.downPaymentPercentage ||
        !this.formData.customerRegion ||
        !this.formData.buildingType
      ) {
        this.upfrontPayment = 0;
      }
      try {
        const res = await CalculatorService.upfrontPayment({
          arnergyProductId: this.activeProduct.id,
          customerRegion: this.formData.customerRegion,
          down_payment_percent: this.formData.downPaymentPercentage,
          buildingType: this.formData.buildingType,
          numInverters: this.formData.numInverters
        });
        this.upfrontPayment = res.data;
      } catch (error) {
        this.upfrontPayment = 0;
      }
    },
    async calculateMonthly() {
      if (!this.formData.numMonths || !this.formData.downPaymentPercentage) {
        this.monthlyPayment = 0;
      }
      try {
        const res = await CalculatorService.monthlyPayment({
          arnergyProductId: this.activeProduct.id,
          down_payment_percent: this.formData.downPaymentPercentage,
          monthly_payment_period: this.formData.numMonths,
          numInverters: this.formData.numInverters
        });
        this.monthlyPayment = res.data;
      } catch (error) {
        this.monthlyPayment = 0;
      }
    },
    async calculateInstallationCost() {
      if (!this.formData.downPaymentPercentage) {
        this.installationCost = 0;
      }
      try {
        const res = await CalculatorService.installationCost({
          arnergyProductId: this.activeProduct.id,
          buildingType: this.formData.buildingType,
          numInverters: this.formData.numInverters
        });
        this.installationCost = res.data;
      } catch (error) {
        this.installationCost = 0;
      }
    },
    async calculateDownPaymentAmount() {
      try {
        // const product = this.$store.state.onboardingCustomerState.selectedProduct;
        const downPayment = await CalculatorService.downPaymentAmount({
          arnergyProductId: this.activeProduct.id,
          down_payment_percent: this.formData.downPaymentPercentage,
          numInverters: this.formData.numInverters
        });
        this.downPaymentAmount = downPayment.data;
        // await this.calculateTotalUpfront();
      } catch (error) {
        this.downPaymentAmount = 0;
      }
    },
    validateDownPayment(rule, value, callback) {
      if (value < this.downPaymentMin) {
        console.log({ value, dp: this.downPaymentMin });
        callback(
          new Error(
            "Down Payment should not be less than 20% of selected equipment cost"
          )
        );
      } else {
        callback();
      }
    },
    async updateDBData(payloadData) {
      try {
        this.showLoading = true;
        const res = await OnboardingService.updateDbData({
          ...payloadData,
          currentPageName: this.nextPageName
        });
        this.showLoading = false;
        if (res) {
          this.navTo(this.nextPageName);
        }
      } catch (error) {
        console.log({ error });
      } finally {
        this.showLoading = false;
      }
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.updateDBData({
            ...this.formData,
            product_id: this.activeProduct.id
          });
        } else {
          // Form validation failed
        }
      });
    }
  },
  mounted() {
    this.activeProduct = this.$store.state.onboardingCustomerState.selectedProduct;
    this.formData.numMonths = default_num_months;
  }
};
</script>

<style lang="scss" scoped>
h1,
h2,
h3,
h4,
p {
  margin: 0;
  padding: 0;
  font-family: "DM Sans", sans-serif;
  font-weight: 400;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.el-input-number {
  width: 100%;
}
.downPayment {
  width: 102% !important;
  label {
    margin: 24px 0;
    color: #727272;
    font-size: 16px;
  }
}
.tenureMonth {
  label {
    margin: 24px 0;
    color: #727272;
    font-size: 16px;
  }
}

.buildingType {
  width: 102% !important;
  label {
    margin: 24px 0;
    color: #727272;
    font-size: 16px;
  }
}

.address {
  width: 102% !important;
  label {
    margin: 24px 0;
    color: #727272;
    font-size: 16px;
    // font-weight: normal;
  }
}

.quantity label {
  font-size: 16px;
  color: #727272;
  // font-weight: normal;
}
.quantity input {
  width: 50px;
  border-radius: 6px;
  height: 30px;
  margin-left: 6px;
  border: 1px solid #d3d9dd;
}

p {
  color: #727272;
  font-size: 16px;
  line-height: 24px;
}

h2 {
  color: #0892d0;
  margin-bottom: 18px;
}

.summary-container {
  width: 100%;
  max-width: 527px;
}

.summary {
  // width: 55%;

  h2 {
    color: #0892d0;
    margin-bottom: 30px;
    font-weight: 700;
  }

  > div {
    width: 100%;
  }

  .summary-table {
    padding: 0px 28px;
    background: #fafafa;

    > div {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 17px 0px;
      border-bottom: 1px dashed #e8e2e2;

      p {
        color: #000000;
        font-size: 16px;
      }

      p:last-child {
        text-align: left;
        width: 30%;
      }

      &:last-child {
        border-bottom: none;
      }
    }

    .bold-text p {
      font-weight: bold;
    }

    .tabbed {
      position: relative;
    }

    .tabbed p {
      margin-left: 55px;
    }

    .tabbed .arrows {
      position: absolute;
      bottom: 30px;
      left: 25px;
    }
  }

  .tenure-section {
    margin-bottom: 30px;

    h4 {
      color: #fe7a11;
      margin-bottom: 10px;
      font-weight: bold;
    }

    .tenure-bar {
      display: flex;
      justify-content: space-between;

      .point {
        text-align: center;
        position: relative;
        width: 15px;
        overflow-x: visible;
        cursor: pointer;

        &:first-child {
          cursor: text;
        }

        &:last-child {
          text-align: right;
        }

        p {
          color: #efefef;
          font-size: 12px;
          line-height: 16px;
          margin-bottom: 2px;
          height: 20px;
        }

        div {
          width: 3px;
          height: 8px;
          border-radius: 2px;
          background: #efefef;
          display: inline-block;
        }

        div.active {
          background: #ace5ff;
        }

        p.marked {
          padding: 2px 8px;
          border-radius: 5px;
          background: #0892d0;
          width: 80px;
          margin-left: -32.5px;
        }

        div.marked {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #0892d0;
        }
      }
    }

    .base-line {
      background: #efefef;
      height: 5px;
      border-radius: 9px;
      margin-top: -14px;

      .blue-line {
        width: 82.5%;
        height: 5px;
        background: #ace5ff;
        border-radius: 9px;
      }
    }
  }
}

.illustration {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  max-width: 350px;

  img:first-child {
    margin-bottom: -3rem;
    z-index: 3;
  }
}

.cost-loading {
  display: grid;
  height: 100px;
  place-content: center;
}

.description {
  max-width: 340px;
  border-radius: 40px;
  padding: 32px 20px;
  background: #e6f6ff;
  text-align: center;
  font-family: "DM Sans";
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: #0892d0;
}

.polygon {
  width: 5.3rem;
  height: 5.8rem;
  margin-left: 21rem;
  margin-top: -1.5rem;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/onboarding/standard/lo/payment-summary/index.vue